import {combineLatest, Observable, of, pipe} from 'rxjs';
import {catchError, concatMap, map, retry, scan, switchMap, takeWhile} from 'rxjs/operators';
import {PageResponse} from '../models/page-response';

type ResponseType<T> = PageResponse<T> | T[];

export function filteredInfiniteScrollObservable<T, F>(
    filterParams: Observable<F>,
    loadMoreSubject: Observable<any>,
    loadFunction: (page: number, filter: F) => Observable<ResponseType<T>>,
): Observable<T[]> {
    return filterParams.pipe(
        switchMap((switchParams: any) => {
            return combineLatest([of(switchParams), loadMoreSubject]).pipe(
                map<[F, void], [F, number]>(([params]) => [params, -1]), // Initial page number of -1
                scan<[F, number]>(([previousParams, previousPage], [currentParams, currentPage]) => {
                    // Calculate page number by sum of previous plus current pagenumber
                    // If previousPage is -1 then start with page 0
                    const newPage = previousPage === -1 ? 0 : previousPage + 1;

                    return [ currentParams, newPage ];
                }, [{} as F, -1]),
                concatMap<[F, number], Observable<ResponseType<T>>>(([params, page]) => loadFunction(page, params)),
                retry(3), // Retry retrieving at most 3 times
                takeWhile<ResponseType<T>>(response => {
                    if (Array.isArray(response)) {
                        return response.length > 0;
                    } else {
                        // Take data while last page hasn't been reached yet
                        return response.number === 0 || response.number < response.totalPages;
                    }
                }),
                // Only return items, not the other paginated properties
                map<ResponseType<T>, T[]>((it) => Array.isArray(it) ? it : it.content),
                scan<T[], T[]>((acc, items) => [...acc, ...items], []),
            );
        }),
        catchError((err) => {
            // Catch error to prevent observable finishing / error-ing
            console.error('Failed retrieving data:', err);
            return of([]);
        }),
    );
}
